import { Deposit } from '../../models/Deposit'
import { Payee } from '../../models/Payee'
import { Payment } from '../../models/Payment'
import { Source } from '../../models/Source'
import { currencyFormat } from '../../utils/numbers'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'

type Props = {
    deposit: Deposit
    payee: Payee
    source: Source
    payments: Payment[]
}

export const DepositView = ({ deposit, payee, source, payments }: Props) => {
    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        <Heading size="lg" className="text-center">
                            {currencyFormat(deposit.amount ?? 0)}
                        </Heading>

                        <Paragraph
                            size="lg"
                            className="flex items-center justify-center gap-x-2 font-normal text-gray-600"
                        >
                            {source.icon} {source.displayTitleWithEnding}
                        </Paragraph>

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {deposit.refnum}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {payee.person && (
                            <BaseRecordView.IconField
                                icon={<ContactAvatar user={payee.person} hasHoverEffect />}
                                title={payee.person.name ?? ''}
                                subtitle="Client"
                                secondaryView={
                                    <a href={`/messages/${payee.person.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                }
                                href={`/clients/${payee.person.id}`}
                            />
                        )}
                    </div>
                </Card>

                <Card size="sm" title="Status">
                    <RecordStatusChecklist
                        items={deposit.statuses.map((status) => ({
                            title: status.title,
                            subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                            status: status.date !== undefined ? 'checked' : 'empty',
                        }))}
                        className="mt-6"
                    />
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <BaseRecordView.PaymentsSection payments={payments} />

                {deposit.memo && (
                    <Card title="Memo">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {deposit.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
